import { useState, useEffect, useCallback } from "react";
import MMAllegroSetupBanner from "./MMAllegroSetupBanner";

const API = "/api";

type Claim = {
  id: string;
  account_id: string;
  account_name: string;
  allegro_claim_id: string | null;
  buyer_login: string;
  commission_amount: number;
  commission_currency: string;
  quantity: number;
  claim_created_at: string | null;
  status: string;
  is_cash_on_delivery: boolean;
  last_appeal_at: string | null;
  notes: string | null;
  offer_id: string | null;
  line_item_id: string | null;
};

type AccountPasswordStatus = {
  account_id: string;
  account_name: string;
  has_password: boolean;
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  rejected:  { label: "Odrzucony",      color: "#e74c3c" },
  appealed:  { label: "Odwołany",       color: "#f39c12" },
  verify:    { label: "Zweryfikuj",     color: "#8e44ad" },
  not_found: { label: "Nie znaleziono", color: "#95a5a6" },
  resolved:  { label: "Rozwiązany",     color: "#27ae60" },
};

const APPEAL_URL = "https://business.allegro.pl/moje-allegro/moje-konto/rachunki/odwolanie-od-decyzji-o-rabacie-transakcyjnym";

const isMobile = () => window.innerWidth < 768;

export default function RefundClaims({ token, onBack, currentUser = null }: { token: string; onBack: () => void; currentUser?: any }) {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [accounts, setAccounts] = useState<AccountPasswordStatus[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [allegroBannerClosed, setAllegroBannerClosed] = useState(false);
  const [justReturnedFromAllegro] = useState(() => {
    try { return localStorage.getItem('mm_return_module') === 'refund-claims'; } catch (e) { return false; }
  });
  useEffect(() => {
    if (justReturnedFromAllegro) { try { localStorage.removeItem('mm_return_module'); } catch (e) {} }
  }, [justReturnedFromAllegro]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("rejected,verify,appealed");

  const setFilterWithContext = (status: string) => {
    setFilterStatus(status);
    const ctx = status.includes('appealed') && !status.includes('rejected') ? 'refund-claims:appealed'
      : status === 'resolved' ? 'refund-claims:resolved'
      : 'refund-claims';
    localStorage.setItem('assistant_context', ctx);
  };
  const [filterAccount, setFilterAccount] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [appealing, setAppealing] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ msg: string; type: "ok" | "err" } | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importAccount, setImportAccount] = useState("");
  const [importLogins, setImportLogins] = useState("");
  const [importing, setImporting] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwords, setPasswords] = useState<Record<string, string>>({});
  const [savingPassword, setSavingPassword] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const mobile = isMobile();

  const notify = (msg: string, type: "ok" | "err" = "ok") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/refund-claims?status=${filterStatus}`;
      if (filterAccount) url += `&account_id=${filterAccount}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      setClaims(await r.json());
    } catch { notify("Błąd pobierania danych", "err"); }
    setLoading(false);
  }, [token, filterStatus, filterAccount]);

  const fetchAccounts = useCallback(async () => {
    const r = await fetch(`${API}/settings/allegro-passwords`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAccounts(await r.json()); setAccountsLoaded(true);
  }, [token]);

  useEffect(() => {
    localStorage.setItem('assistant_context', 'refund-claims');
    return () => { localStorage.removeItem('assistant_context'); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchClaims(); }, [fetchClaims]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAppeal = async (claim: Claim) => {
    const acc = accounts.find(a => a.account_id === claim.account_id);
    if (!acc?.has_password) {
      notify("Brak hasła dla konta " + claim.account_name + " — dodaj w Hasłach kont", "err");
      return;
    }
    setAppealing(claim.id);
    try {
      const r = await fetch(`${API}/refund-claims/${claim.id}/appeal`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      if (data.result === "appealed") notify("✓ Odwołanie złożone dla " + claim.buyer_login);
      else if (data.result === "verify") notify("⚠ Wymagana ręczna weryfikacja dla " + claim.buyer_login, "err");
      else if (data.result === "not_found") notify("Brak wniosku na Allegro dla " + claim.buyer_login);
      else notify("Błąd: " + data.message, "err");
      fetchClaims();
    } catch { notify("Błąd połączenia", "err"); }
    setAppealing(null);
  };

  const handleManualVerify = async (claim: Claim) => {
    setHighlightedId(claim.id);
    // Kopiuj do schowka login lub offer_id
    const searchTerm = claim.buyer_login || claim.offer_id || "";
    if (searchTerm) {
      try {
        await navigator.clipboard.writeText(searchTerm);
      } catch {}
    }
    window.open(APPEAL_URL, "_blank");
    const hint = claim.buyer_login
      ? `Login "${claim.buyer_login}" skopiowany do schowka.`
      : claim.offer_id
      ? `Nr oferty "${claim.offer_id}" skopiowany do schowka.`
      : "";
    notify(`Otwarto stronę Allegro (konto: ${claim.account_name}). ${hint} Wklej w pole wyszukiwania i kliknij ODWOŁAJ SIĘ.`);
  };

  const handleStatusChange = async (claim: Claim, status: string) => {
    await fetch(`${API}/refund-claims/${claim.id}`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ status })
    });
    fetchClaims();
  };

  const handleCODToggle = async (claim: Claim) => {
    await fetch(`${API}/refund-claims/${claim.id}`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ is_cash_on_delivery: !claim.is_cash_on_delivery })
    });
    fetchClaims();
  };

  const handleImport = async () => {
    if (!importAccount || !importLogins.trim()) return;
    setImporting(true);
    try {
      const r = await fetch(`${API}/refund-claims/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: importAccount, buyer_logins: importLogins })
      });
      const data = await r.json();
      notify(`Dodano: ${data.added}, pominięto: ${data.skipped}`);
      setImportLogins(""); setShowImport(false); fetchClaims();
    } catch { notify("Błąd importu", "err"); }
    setImporting(false);
  };

  const handleSavePassword = async (account_id: string) => {
    const pwd = passwords[account_id];
    if (!pwd) return;
    setSavingPassword(account_id);
    try {
      await fetch(`${API}/settings/allegro-password`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ account_id, password: pwd })
      });
      notify("Hasło zapisane");
      setPasswords(p => ({ ...p, [account_id]: "" }));
      fetchAccounts();
    } catch { notify("Błąd zapisu hasła", "err"); }
    setSavingPassword(null);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${API}/refund-claims/sync-from-allegro`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      notify(`Sync: dodano ${data.added}, zaktualizowano ${data.updated}`);
      fetchClaims();
    } catch { notify("Błąd sync", "err"); }
    setSyncing(false);
  };

  const commissionByCurrency = claims
    .filter(c => c.status !== "not_found" && c.status !== "resolved")
    .reduce((acc, c) => {
      const cur = c.commission_currency || "PLN";
      acc[cur] = (acc[cur] || 0) + c.commission_amount;
      return acc;
    }, {} as Record<string, number>);
  const commissionSummary = Object.entries(commissionByCurrency)
    .map(([cur, amt]) => `${amt.toFixed(2)} ${cur}`)
    .join(", ");

  return (
    <>
      {((accountsLoaded && accounts.length === 0) || justReturnedFromAllegro) && !allegroBannerClosed && (
        <MMAllegroSetupBanner
          message="Aby zarządzać odrzuconymi zgłoszeniami, podłącz konto Allegro."
          returnModule="refund-claims"
          showCongrats={justReturnedFromAllegro}
          onClose={() => setAllegroBannerClosed(true)}
        />
      )}
    <div style={{ padding: mobile ? "12px" : "20px", fontFamily: "sans-serif", maxWidth: 1200, margin: "0 auto" }}>

      {/* Nagłówek */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280" }}>←</button>
        <div>
          <div style={{ fontWeight: 700, fontSize: mobile ? 18 : 22 }}>Odrzucone</div>
          <div style={{ fontSize: 11, color: "#666" }}>wnioski o zwrot prowizji</div>
        </div>
      </div>

      {/* Przyciski akcji */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {false && <button onClick={() => { setShowPasswords(!showPasswords); setShowImport(false); localStorage.setItem('assistant_context', !showPasswords ? 'refund-claims:passwords' : 'refund-claims'); }} style={btn("#8e44ad")}>🔑 Hasła kont</button>}
        <button onClick={() => { setShowImport(!showImport); setShowPasswords(false); localStorage.setItem('assistant_context', !showImport ? 'refund-claims:import' : 'refund-claims'); }} style={btn("#2980b9")}>+ Dodaj ręcznie</button>
        <button onClick={handleSync} disabled={syncing} style={btn("#27ae60")}>{syncing ? "Sync..." : "↻ Sync z Allegro"}</button>
      </div>

      {/* Powiadomienie */}
      {notification && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, background: notification.type === "ok" ? "#d4edda" : "#f8d7da", color: notification.type === "ok" ? "#155724" : "#721c24", fontSize: 13 }}>
          {notification.msg}
        </div>
      )}

      {/* Panel haseł */}
      {showPasswords && (
        <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>🔑 Hasła do kont Allegro</div>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>Hasła są szyfrowane. Używane tylko do automatycznych odwołań.</div>
          {accounts.map(acc => (
            <div key={acc.account_id} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 14, minWidth: 100 }}>{acc.account_name}</span>
                <span style={{ fontSize: 12, color: acc.has_password ? "#27ae60" : "#e74c3c" }}>
                  {acc.has_password ? "✓ ustawione" : "✗ brak"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  type="password"
                  placeholder="Nowe hasło..."
                  value={passwords[acc.account_id] || ""}
                  onChange={e => setPasswords(p => ({ ...p, [acc.account_id]: e.target.value }))}
                  style={{ flex: 1, padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}
                />
                <button
                  onClick={() => handleSavePassword(acc.account_id)}
                  disabled={!passwords[acc.account_id] || savingPassword === acc.account_id}
                  style={btn("#27ae60")}
                >
                  {savingPassword === acc.account_id ? "..." : "Zapisz"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Import manualny */}
      {showImport && (
        <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>+ Dodaj wnioski ręcznie</div>
          <select
            value={importAccount}
            onChange={e => setImportAccount(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, marginBottom: 8 }}
          >
            <option value="">Wybierz konto Allegro...</option>
            {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
          </select>
          <textarea
            value={importLogins}
            onChange={e => setImportLogins(e.target.value)}
            placeholder={"Wklej loginy (jeden per linia):\nkupujacy1\nkupujacy2\nClient:123456"}
            style={{ width: "100%", height: 100, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box", resize: "vertical" }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button onClick={handleImport} disabled={importing || !importAccount || !importLogins.trim()} style={btn("#2980b9")}>
              {importing ? "Importuję..." : "Importuj"}
            </button>
            <button onClick={() => setShowImport(false)} style={btn("#95a5a6")}>Anuluj</button>
          </div>
        </div>
      )}

      {/* Filtry */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <select value={filterStatus} onChange={e => setFilterWithContext(e.target.value)}
          style={{ flex: 1, minWidth: 160, padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}>
          <option value="rejected,verify,appealed">Aktywne</option>
          <option value="rejected">Odrzucone</option>
          <option value="verify">Do weryfikacji</option>
          <option value="appealed">Odwołane</option>
          <option value="not_found">Nie znaleziono</option>
          <option value="resolved">Rozwiązane</option>
          <option value="">Wszystkie</option>
        </select>
        <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}
          style={{ flex: 1, minWidth: 130, padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}>
          <option value="">Wszystkie konta</option>
          {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
        </select>
      </div>

      {/* Statystyki */}
      <div style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>
        {claims.length} wniosków | Łączna prowizja: <strong style={{ color: "#e74c3c" }}>{commissionSummary || "0.00 PLN"}</strong>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#666" }}>Ładowanie...</div>
      ) : claims.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#666" }}>Brak wniosków</div>
      ) : mobile ? (
        /* MOBILE — karty */
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {claims.map(claim => {
            const st = STATUS_LABELS[claim.status] || { label: claim.status, color: "#666" };
            return (
              <div key={claim.id} style={{ background: highlightedId === claim.id ? "#fff3cd" : "#fff", border: highlightedId === claim.id ? "1px solid #f0c040" : "1px solid #e5e7eb", borderRadius: 10, padding: 14, transition: "all 0.3s" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{claim.account_name}</div>
                    <div style={{ fontSize: 12, color: "#666", fontFamily: "monospace" }}>{claim.buyer_login}</div>
                  </div>
                  <span style={{ background: st.color, color: "#fff", padding: "3px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                    {st.label}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 13 }}>
                  <div>
                    <div style={{ color: "#999", fontSize: 11 }}>Prowizja</div>
                    <div style={{ fontWeight: 700, color: "#e74c3c" }}>{claim.commission_amount.toFixed(2)} {claim.commission_currency}</div>
                  </div>
                  <div>
                    <div style={{ color: "#999", fontSize: 11 }}>Data wniosku</div>
                    <div>{claim.claim_created_at ? new Date(claim.claim_created_at).toLocaleDateString("pl-PL") : "—"}</div>
                  </div>
                  <div>
                    <div style={{ color: "#999", fontSize: 11 }}>Pobranie</div>
                    <input type="checkbox" checked={claim.is_cash_on_delivery} onChange={() => handleCODToggle(claim)} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {(claim.status === "rejected" || claim.status === "verify") && (
                    <button onClick={() => handleManualVerify(claim)} style={btn(claim.status === "verify" ? "#8e44ad" : "#e74c3c")}>
                      {claim.status === "verify" ? "Odwołaj ręcznie" : "Odwołaj"}
                    </button>
                  )}
                  {claim.status === "rejected" && (
                    <button onClick={() => handleStatusChange(claim, "appealed")} style={btn("#f39c12")}>✓ Złożono odwołanie</button>
                  )}
                  {claim.status === "appealed" && (
                    <button onClick={() => handleStatusChange(claim, "resolved")} style={btn("#27ae60")}>✓ Rozwiązano</button>
                  )}
                  {(claim.status === "rejected" || claim.status === "verify" || claim.status === "appealed") && (
                    <button onClick={() => handleStatusChange(claim, "resolved")} style={btn("#6b7280")}>✕ Zamknij</button>
                  )}
                  {(claim.status === "not_found" || claim.status === "resolved") && (
                    <button onClick={() => handleStatusChange(claim, "rejected")} style={btn("#95a5a6")}>Przywróć</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* DESKTOP — tabela */
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f1f3f4" }}>
                {["Konto", "Kupujący", "Prowizja", "Data wniosku", "Status", "Pobranie", "Ostatnie odwołanie", "Akcje"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#444", borderBottom: "2px solid #dee2e6", textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {claims.map(claim => {
                const st = STATUS_LABELS[claim.status] || { label: claim.status, color: "#666" };
                return (
                  <tr key={claim.id} style={{ borderBottom: "1px solid #eee", background: highlightedId === claim.id ? "#fff3cd" : "transparent", transition: "background 0.3s" }}>
                    <td style={td}><strong>{claim.account_name}</strong></td>
                    <td style={td}><span style={{ fontFamily: "monospace", fontSize: 12 }}>{claim.buyer_login}</span></td>
                    <td style={td}><strong style={{ color: "#e74c3c" }}>{claim.commission_amount.toFixed(2)} {claim.commission_currency}</strong></td>
                    <td style={td}>{claim.claim_created_at ? new Date(claim.claim_created_at).toLocaleDateString("pl-PL") : "—"}</td>
                    <td style={td}><span style={{ background: st.color, color: "#fff", padding: "3px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{st.label}</span></td>
                    <td style={{ ...td, textAlign: "center" }}><input type="checkbox" checked={claim.is_cash_on_delivery} onChange={() => handleCODToggle(claim)} /></td>
                    <td style={td}>{claim.last_appeal_at ? new Date(claim.last_appeal_at).toLocaleDateString("pl-PL") : "—"}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {(claim.status === "rejected" || claim.status === "verify") && <button onClick={() => handleManualVerify(claim)} style={btn(claim.status === "verify" ? "#8e44ad" : "#e74c3c")}>{claim.status === "verify" ? "Odwołaj ręcznie" : "Odwołaj"}</button>}
                      {claim.status === "rejected" && <button onClick={() => handleStatusChange(claim, "appealed")} style={btn("#f39c12")}>✓ Złożono odwołanie</button>}
                      {claim.status === "appealed" && <button onClick={() => handleStatusChange(claim, "resolved")} style={btn("#27ae60")}>✓ Rozwiązano</button>}
                      {(claim.status === "rejected" || claim.status === "verify" || claim.status === "appealed") && <button onClick={() => handleStatusChange(claim, "resolved")} style={{...btn("#6b7280"), marginLeft: 4}}>✕ Zamknij</button>}
                      {(claim.status === "not_found" || claim.status === "resolved") && <button onClick={() => handleStatusChange(claim, "rejected")} style={btn("#95a5a6")}>Przywróć</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </>
  );
}

const btn = (bg: string): React.CSSProperties => ({
  background: bg, color: "#fff", border: "none", borderRadius: 6,
  padding: "7px 12px", fontSize: 13, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap"
});

const td: React.CSSProperties = { padding: "10px 12px", verticalAlign: "middle" };
